import React from "react"
import { Link, Typography } from "@mui/joy"
import {
    HighlightBox,
    LegalFooter,
    LegalHeader,
    LegalHero,
    LegalList,
    LegalMain,
    LegalPage,
    LegalSection,
    LegalText,
    LegalToc,
} from "../components/LegalLayout"

const tocItems = [
    { href: "#acceptance", label: "Acceptance of Terms" },
    { href: "#description", label: "Description of Service" },
    { href: "#eligibility", label: "Eligibility" },
    { href: "#account", label: "Account Registration" },
    { href: "#subscription", label: "Subscription & Payments" },
    { href: "#acceptable-use", label: "Acceptable Use" },
    { href: "#ip", label: "Intellectual Property" },
    { href: "#ai", label: "AI-Generated Content" },
    { href: "#audio", label: "Audio Data" },
    { href: "#termination", label: "Termination" },
    { href: "#disclaimer", label: "Disclaimer of Warranties" },
    { href: "#liability", label: "Limitation of Liability" },
    { href: "#law", label: "Governing Law" },
    { href: "#changes", label: "Changes to These Terms" },
]

const TermsOfUse = () => (
    <LegalPage>
        <LegalHeader />
        <LegalHero title="Terms of Use" />

        <LegalMain>
            <LegalToc items={tocItems} />

            <LegalSection id="acceptance" num="01" title="Acceptance of Terms">
                <LegalText>
                    By accessing or using LinguaScore, you agree to be bound by these Terms of Use. If you do not
                    agree to these terms, you must not use the platform. LinguaScore is operated by ZeeJay Labs,
                    based in Douala, Cameroon.
                </LegalText>
            </LegalSection>

            <LegalSection id="description" num="02" title="Description of Service">
                <LegalText>
                    LinguaScore is an AI-powered language exam preparation platform designed to help users prepare for
                    the IELTS examination. The platform provides practice tests, AI-graded feedback, speaking
                    simulations, and study resources across the four IELTS sections: Listening, Reading, Writing, and
                    Speaking.
                </LegalText>
            </LegalSection>

            <LegalSection id="eligibility" num="03" title="Eligibility">
                <LegalText>
                    You must be at least 16 years of age to use LinguaScore. By creating an account, you confirm that
                    you meet this age requirement. If you are under 18, you confirm that you have obtained parental or
                    guardian consent to use the platform.
                </LegalText>
            </LegalSection>

            <LegalSection id="account" num="04" title="Account Registration">
                <LegalText>
                    You may register using a valid email address or through a supported third-party authentication
                    provider such as Google or Facebook. You are responsible for maintaining the confidentiality of
                    your account and for all activity that occurs under it.
                </LegalText>
                <HighlightBox variant="warning">
                    <Typography>
                        You must not share your account with any other person. Account sharing is a violation of these
                        Terms and may result in immediate suspension.
                    </Typography>
                </HighlightBox>
            </LegalSection>

            <LegalSection id="subscription" num="05" title="Subscription & Payments">
                <LegalText>
                    Access to certain features of LinguaScore requires a paid subscription. Subscription fees are
                    charged in advance on a monthly or yearly basis depending on the plan selected. All payments are
                    processed through secure third-party payment providers including Flutterwave and iKeepay.
                </LegalText>
                <LegalText>
                    Subscription fees are non-refundable except where required by applicable law or as stated in our
                    Refund Policy.
                </LegalText>
            </LegalSection>

            <LegalSection id="acceptable-use" num="06" title="Acceptable Use">
                <LegalText>
                    You agree to use LinguaScore only for lawful purposes and in accordance with these Terms. You must
                    not:
                </LegalText>
                <LegalList items={[
                    "Share your account credentials with any other person",
                    "Attempt to circumvent or manipulate the AI grading system",
                    "Use the platform to distribute harmful, offensive, or unlawful content",
                    "Attempt to reverse engineer, copy, or replicate any part of the platform",
                    "Use automated tools, bots, or scrapers to access the platform",
                    "Misrepresent your identity or impersonate another person",
                    "Interfere with or disrupt the integrity or performance of the platform",
                ]} />
            </LegalSection>

            <LegalSection id="ip" num="07" title="Intellectual Property">
                <LegalText>
                    All content on LinguaScore, including but not limited to exam questions, audio recordings,
                    feedback text, design elements, and software, is the intellectual property of ZeeJay Labs and is
                    protected by applicable copyright and intellectual property laws. You may not reproduce,
                    distribute, or create derivative works from any content on the platform without our prior written
                    consent.
                </LegalText>
            </LegalSection>

            <LegalSection id="ai" num="08" title="AI-Generated Content">
                <HighlightBox>
                    <Typography>
                        LinguaScore uses artificial intelligence to generate feedback and grading results.
                        AI-generated feedback is provided for educational purposes only and does not constitute a
                        guarantee of any particular exam outcome. Results may vary from official IELTS examination
                        scores.
                    </Typography>
                </HighlightBox>
            </LegalSection>

            <LegalSection id="audio" num="09" title="Audio Data">
                <LegalText>
                    By using the speaking test feature, you consent to the recording and processing of your voice for
                    the sole purpose of generating AI feedback. Audio recordings are not shared with third parties
                    beyond our transcription provider and are deleted within 90 days as described in our Data
                    Retention Policy.
                </LegalText>
            </LegalSection>

            <LegalSection id="termination" num="10" title="Termination">
                <LegalText>
                    We reserve the right to suspend or terminate your account at any time if we determine that you
                    have violated these Terms of Use. You may also delete your account at any time through the
                    platform settings. Upon termination, your access to the platform will cease immediately.
                </LegalText>
            </LegalSection>

            <LegalSection id="disclaimer" num="11" title="Disclaimer of Warranties">
                <LegalText>
                    LinguaScore is provided on an "as is" basis without warranties of any kind, either express or
                    implied. We do not guarantee that the platform will be available at all times or that it will be
                    free from errors or interruptions.
                </LegalText>
            </LegalSection>

            <LegalSection id="liability" num="12" title="Limitation of Liability">
                <LegalText>
                    To the fullest extent permitted by applicable law, ZeeJay Labs shall not be liable for any
                    indirect, incidental, special, or consequential damages arising from your use of the platform,
                    including but not limited to loss of data, loss of revenue, or failure to achieve a particular
                    exam score.
                </LegalText>
            </LegalSection>

            <LegalSection id="law" num="13" title="Governing Law">
                <LegalText>
                    These Terms of Use shall be governed by and construed in accordance with the laws of the Republic
                    of Cameroon. Any disputes arising from these terms shall be subject to the exclusive jurisdiction
                    of the courts of Douala, Cameroon.
                </LegalText>
            </LegalSection>

            <LegalSection id="changes" num="14" title="Changes to These Terms">
                <LegalText>
                    We may update these Terms of Use from time to time. Continued use of the platform after any
                    changes constitutes your acceptance of the updated terms. We will notify you of material changes
                    via email or through the platform.
                </LegalText>
                <HighlightBox>
                    <Typography>
                        Questions? Contact us at{" "}
                        <Link href="mailto:legal@linguascores.com" sx={{ color: "var(--ls-pink)" }}>
                            legal@linguascores.com
                        </Link>{" "}
                        — ZeeJay Labs, Douala, Cameroon.
                    </Typography>
                </HighlightBox>
            </LegalSection>
        </LegalMain>

        <LegalFooter />
    </LegalPage>
)

export default TermsOfUse
